// perfMeasure.ts — drawing-core hot-path instrumentation hook.
//
// 2026-05-17 (wide-eraser-perf-investigation-2026-05-16.md §「第 2 ラウンド」
// + drawing-perf-baseline-2026-05-17.md):
//
// `eraseLine` / `drawLine` 周辺の wall-clock を `performance.mark` +
// `performance.measure` で in-page 観測できるようにする. `packages/web/e2e/
// drawingPerfHarness.spec.ts` がブラウザ上で `enableDrawingPerfMeasure()` を
// 呼んだ後にストロークを駆動、終端で `performance.getEntriesByType('measure')`
// を回収して JSON dump に焼く. baseline 数値はこの経路で取った.
//
// 設計:
//
//   1. デフォルト OFF — `ENABLED` モジュール変数は初期 `false`. `eraseLine` /
//      `drawLine` の hot loop に挟むのは boolean check 1 個だけ. production
//      bundle (= `enableDrawingPerfMeasure` を呼ばない) では分岐の thru-path
//      のみ走り、measure / mark の重い API には触らない.
//
//   2. ON 時の overhead — `performance.mark()` / `performance.measure()` は
//      User Timing API 標準仕様で modern browser だと数 µs / call. 太い消し
//      ゴム 1 segment が 50ms 単位の世界なので、計測自体の overhead は
//      ノイズ未満 (ベースライン 50ms × 1% < 1ms).
//
//   3. ナビゲーション独立 — `performance` グローバルは:
//        - browser main thread / worker thread に存在
//        - Node 16+ も `node:perf_hooks` 経由で `globalThis.performance` 提供
//          (vitest run でも事故らない)
//        - ない環境では noop に落とす (defensive)
//
//   4. DCE しない理由 — drawing-core は `tsc -b` で transpile しているので
//      Vite の `import.meta.env.DEV` のような bundler-time DCE は drawing-core
//      package 内では効かない. web package に bundle される段で Vite が拾うが、
//      module-level の `let ENABLED = false` + `if (!ENABLED) return` パターン
//      なら本番でも boolean check 1 個の overhead に圧縮できる. これで十分.
//      Wasm 移行 (Phase 12+, architecture.md §19.12) では measure 経路ごと
//      Wasm side に移すか、その時点で再設計する.
//
// 使い方:
//
//   ```ts
//   // E2E spec / Playwright `page.evaluate` 上で:
//   import { enableDrawingPerfMeasure } from '@oekaki/drawing-core';
//   enableDrawingPerfMeasure();
//   // ...stroke を駆動...
//   const entries = performance.getEntriesByType('measure')
//     .filter(e => e.name.startsWith('oekaki.drawing.'));
//   // → totalMs / count / max を集計
//   ```
// ─── Mutable enable flag ──────────────────────────────────────────────────
let ENABLED = false;
let collected = [];
/**
 * Enable `performance.measure` instrumentation in `eraseLine` / `drawLine`.
 *
 * Idempotent. Until called (= production default) the rasterizer's measure
 * branch is short-circuited by a single boolean check; no `performance.*`
 * calls happen. Designed to be flipped from the e2e harness or a DevTools
 * Console probe ("paste this and reload") — never from production code.
 */
export const enableDrawingPerfMeasure = () => {
    ENABLED = true;
};
/**
 * Disable measurement (= back to production-default short-circuit). Use
 * between harness cells if you want to reset accumulated entries with
 * `clearMeasures()`.
 */
export const disableDrawingPerfMeasure = () => {
    ENABLED = false;
};
/** Lightweight read for tests/diagnostics. Not on the hot path. */
export const isDrawingPerfMeasureEnabled = () => ENABLED;
let cachedPerf;
const getPerf = () => {
    if (cachedPerf !== undefined)
        return cachedPerf;
    const g = globalThis;
    cachedPerf = g.performance ?? null;
    return cachedPerf;
};
// ─── Measure prefix ───────────────────────────────────────────────────────
/**
 * All drawing-core `performance.measure` entries start with this prefix so
 * `page.evaluate(() => performance.getEntriesByType('measure').filter(...))`
 * can drop unrelated entries (= React render measures from
 * `react-dom/client.js`, Sentry tracing init, etc).
 */
export const DRAWING_PERF_MEASURE_PREFIX = 'oekaki.drawing.';
// Standard measure names (frozen list — keep in sync with the harness).
//
// 2026-05-17 追補 (drawing-perf-trace-analysis-2026-05-17.md §「PR 1.6 仮説検証
// 計画」): trace 解析で `compositeForDisplay` (= 2 層レンダリング表示合成、
// 毎 rAF) が bundle 内 JS 時間の 68-82% を占めると判明. PR 1.6 で同関数を
// harness 経路で in-page measure し、`eraseLine` / `drawLine` 合計と比較して
// trace 仮説 (= compositor が真の主犯) を検証する.
export const DRAWING_PERF_MEASURE_NAMES = Object.freeze({
    drawLine: 'oekaki.drawing.drawLine',
    eraseLine: 'oekaki.drawing.eraseLine',
    compositeForDisplay: 'oekaki.drawing.compositeForDisplay',
});
// Mark names live alongside the measure name with a `.start` suffix. We
// keep them as their own const so a future refactor can build a token-typed
// mapping if needed.
const startMarkOf = (measureName) => `${measureName}.start`;
// ─── Public wrapper used by the rasterizer hot path ──────────────────────
/**
 * Wrap a void-returning synchronous function with a `performance.measure`
 * **only when** the global enable flag is set. The default-OFF path is a
 * single boolean check + the original function call — measurable overhead
 * is in the low-ns range and should not register on the harness scale.
 *
 * Designed to be called once per `eraseLine` / `drawLine` invocation
 * (= per Stroke Segment in the wire protocol). That granularity gives the
 * harness "per-segment ms" stats which median-aggregate cleanly across the
 * 3-run × 6-cell workload matrix.
 *
 * `name` MUST start with `DRAWING_PERF_MEASURE_PREFIX` so the harness
 * filter is one substring check.
 */
export const measureDrawingHotPath = (name, fn) => {
    if (!ENABLED) {
        fn();
        return;
    }
    const perf = getPerf();
    if (perf === null) {
        fn();
        return;
    }
    const startMark = startMarkOf(name);
    try {
        perf.mark(startMark);
    }
    catch {
        // Defensive: if `performance.mark` throws (= name collision with a
        // user-installed cleared mark, etc.), fall through and run the fn so
        // measurement never affects correctness.
        fn();
        return;
    }
    // Capture start before fn so we can push a buffer entry even if
    // `performance.measure` throws (= the harness still gets the correct
    // duration without depending on the PerformanceTimeline). `perf.now()`
    // is a high-res monotonic clock on every supported runtime.
    let startTime;
    try {
        startTime = perf.now();
    }
    catch {
        startTime = 0;
    }
    try {
        fn();
    }
    finally {
        let endTime;
        try {
            endTime = perf.now();
        }
        catch {
            endTime = startTime;
        }
        try {
            perf.measure(name, startMark);
        }
        catch {
            // ignored — measure naming clashes shouldn't break drawing
        }
        try {
            perf.clearMarks(startMark);
        }
        catch {
            // ignored
        }
        // Buffer the entry for Worker-offload bridge. We push even if
        // `performance.measure` above threw — the buffer is the authoritative
        // path for harness collection in worker mode.
        collected.push({
            name,
            startTime,
            duration: Math.max(0, endTime - startTime),
        });
    }
};
/**
 * Convenience wrapper for tests/harnesses to clear out all drawing-core
 * measures (the harness calls this between candidate runs so per-cell
 * `getEntriesByType('measure')` reads are clean).
 *
 * Safe to call when disabled / when `performance` is absent.
 *
 * Also drains the in-process Worker-offload buffer (= keeps PerformanceTimeline
 * and `collected[]` in sync — otherwise stale entries would leak between
 * harness candidate runs).
 */
export const clearDrawingPerfMeasures = () => {
    collected = [];
    const perf = getPerf();
    if (perf === null)
        return;
    for (const name of Object.values(DRAWING_PERF_MEASURE_NAMES)) {
        try {
            perf.clearMeasures(name);
        }
        catch {
            // ignored
        }
    }
};
// ─── Worker-offload bridge accessors ─────────────────────────────────────
//
// PR 2-β follow-up (2026-05-21): the e2e harness uses these to collect
// measures from the Worker thread via postMessage round-trip + re-emit on
// the main thread's PerformanceTimeline so existing
// `performance.getEntriesByType('measure')` aggregation keeps working.
//
// Calling cadence: the harness drains the buffer **once per candidate run**
// (= 1 postMessage per ~50 strokes, not per measure entry). Worker-internal
// stamp / drawLine hot loops never touch the wire.
/**
 * Return a shallow-cloned snapshot of every measure collected since the
 * last `clearCollectedDrawingMeasures()` / `clearDrawingPerfMeasures()`
 * call. Order matches insertion (= chronological).
 *
 * Safe when disabled (= returns an empty array). Buffer is **not** drained
 * by this call — pair it with `clearCollectedDrawingMeasures()` when you
 * want a one-shot read.
 */
export const getCollectedDrawingMeasures = () => {
    // Shallow copy so callers don't see internal pushes (= harness reads at
    // a stable moment, even if more strokes fire while it's serialising).
    return collected.slice();
};
/**
 * Drain the in-process measure buffer. Idempotent / safe when disabled.
 * Independent of `clearDrawingPerfMeasures()` so a harness can choose to
 * keep PerformanceTimeline entries (e.g. for Chrome DevTools inspection)
 * while resetting the worker-bridge buffer.
 */
export const clearCollectedDrawingMeasures = () => {
    collected = [];
};
//# sourceMappingURL=perfMeasure.js.map